/**
 * useOpenTrainingWithSession
 * 
 * Safe training artifact opener that:
 * 1. Opens window IMMEDIATELY (popup-safe, synchronous in click context)
 * 2. Creates session row FIRST to get sessionId
 * 3. Logs 'opened' event WITH sessionId for correlation
 * 
 * This ensures:
 * - No popup blocking (window.open is first line)
 * - Session correlation from first event
 * - Fire-and-forget pattern for all async work
 */

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useTrainingLaunchFallbackStore } from '@/stores/trainingLaunchFallbackStore';
import { useTrainingEvents } from './useTrainingEvents';
import {
  useTrainingSessionToken,
  type MintTrainingSessionTokenResult,
} from './useTrainingSessionToken';
import { useTrainingMonitoringConsentGate } from '@/contexts/TrainingMonitoringConsentProvider';
import { useTrainingLaunchDisclosure } from '@/contexts/TrainingLaunchDisclosureProvider';
import { emitCompanionTokenHandoff } from '@/lib/trainingCompanion/companionTokenHandoff';
import type { TrainingEventSource, TrainingEventSurface } from '@/types/training';
import { insertLaunchSessionOnce, newLaunchKey } from '@/lib/training/launchSessionOnce';

// Batch 18-E4: launch_source enum mirrors the CHECK allow-list on
// training_consumption_sessions.tcs_valid_launch_source.
// Batch 18-E6 widens it with the six additional production surfaces
// converged through this hook (see migration 20260603230000).
export type TrainingLaunchSource =
  | 'ticket_training_tab'
  | 'passport_recommendation_card'
  | 'unknown'
  | 'ticket_learning_panel'
  | 'learning_library'
  | 'learning_requirements'
  | 'browse_catalog'
  | 'diagnostic_wizard'
  | 'diagnostic_guidance';

export interface OpenTrainingOptions {
  technicianProfileId: string;
  artifactId: string;
  artifactType: string;
  expectedDurationMinutes: number | null;
  surface: TrainingEventSurface;
  source: TrainingEventSource;
  targetUrl: string;
  ticketId?: string;
  // K108-Y: RESUME an existing pending training session instead of inserting a new
  // one. When set, no new training_consumption_sessions row is created (no
  // duplicate History) — the Companion token is minted for THIS session with
  // resume:true (owner rotates the active token) and resumed evidence appends to
  // it. The mint edge function enforces ownership + mintable-status.
  existingSessionId?: string;
  jobIds?: string[];
  jobCategory?: string;
  /** Core-100 R3 CORE-O: the person's stated intent travels with the durable session (formerly a 2nd store). */
  intent?: 'curiosity' | 'job_help' | 'skill_growth' | 'required';
  // Batch 18-E4: fine-grained UI surface the click came from. Optional;
  // omitting it leaves launch_source NULL on the session row (CHECK
  // allows NULL).
  launchSource?: TrainingLaunchSource;
  // Batch 18-E6: anchor-based surfaces (<a target="_blank">) let the
  // browser handle navigation natively. Setting this flag skips the
  // hook's internal window.open call so the same click does not open
  // two tabs. Tracking (session INSERT + 'opened' event) still happens.
  // Default (undefined / false) preserves the popup-safe button path
  // used by TicketTrainingTab and the passport recommendation card.
  skipWindowOpen?: boolean;
  // K108-P: when true (Companion installed + approved provider URL), the app does
  // NOT open the provider tab — the Companion opens+owns it (tabId) so it can
  // monitor the real training tab. Falls back to app-open if the Companion fails.
  delegateProviderTabToCompanion?: boolean;
  // K108-P: approved-domain allowlist handed to the Companion for validation.
  approvedDomains?: string[] | null;
  // Batch 18-F9B: optional handoff seam for the future Training
  // Companion browser extension (18-F10). When a session is created
  // and a Companion token is successfully minted, this callback is
  // invoked with { sessionId, token, expiresAt }. The hook itself
  // does NOT store the token, postMessage it, or persist it anywhere
  // — the seam exists so 18-F10 can attach extension handoff without
  // re-architecting this hook. Mint failures are swallowed and the
  // callback is NOT invoked on failure. Omitting this field (the
  // default for all current call sites) is fully backward compatible:
  // the token mint still runs in the background, but its result is
  // discarded.
  onCompanionTokenMinted?: (result: MintTrainingSessionTokenResult) => void;
}

export function useOpenTrainingWithSession() {
  const queryClient = useQueryClient();
  const { logOpenedFireAndForget } = useTrainingEvents();
  // Batch 18-F9B: top-level mutation hook so the rules-of-hooks stay
  // satisfied. The mutation itself is invoked from inside the async
  // IIFE below (mutations may be triggered from callbacks; only hooks
  // must stay at the top level).
  const tokenMutation = useTrainingSessionToken();
  // TCX-8 (K78-C): consent gate for monitored launches. Null when the provider
  // is not mounted (e.g. isolated unit tests) → launches run directly, so
  // existing surface tests are unaffected.
  const consentGate = useTrainingMonitoringConsentGate();
  // K108-R Batch 1: per-launch monitoring disclosure — shown EVERY monitored
  // launch, before the durable-consent gate. Null when unmounted (unit tests) →
  // launch proceeds directly, so existing surface tests are unaffected.
  const launchDisclosure = useTrainingLaunchDisclosure();

  /**
   * Open training artifact - SAFE from popup blocking
   *
   * Pattern: Open window IMMEDIATELY, then fire-and-forget session creation + event logging
   *
   * @param options - Training artifact options including targetUrl
   */
  const openTraining = (options: OpenTrainingOptions): void => {
    // TCX-8: the launch below mints a Companion MONITORING token (step 4), so it
    // is gated behind durable opt-in consent. For an already-consented user the
    // gate runs `runLaunch` SYNCHRONOUSLY (popup-safe); otherwise it opens the
    // consent modal and only runs `runLaunch` after a successful opt-in. The
    // server mint gate remains the final authority.
    const runLaunch = (preOpenedWindow?: Window | null) => {
    // STEP 1: OPEN WINDOW IMMEDIATELY (must be synchronous, no await before this)
    // This is critical - any await before window.open can cause popup blocking.
    // 18-E6: anchor-based surfaces pass skipWindowOpen=true so the <a>
    // element handles navigation natively (preserving middle-click,
    // right-click, link preview); calling window.open here would
    // double-open. The hook still performs tracking after this guard.
    //
    // TCX-3 (K108-L / Safari popup-safety): when a monitored launch goes through
    // the consent modal, the provider tab is opened SYNCHRONOUSLY inside the
    // "Agree & continue" click and passed in as `preOpenedWindow`. Safari
    // invalidates the user activation across the `await` on the consent write,
    // so a fresh window.open here is silently popup-blocked. If we were given a
    // pre-opened tab, we NAVIGATE it instead of opening a new one. Chrome's
    // already-consented / direct path passes nothing and opens directly as
    // before (still synchronous in the originating click).
    // K108-P: when the Companion is installed and the provider URL is approved,
    // DELEGATE tab-opening to the Companion so it owns the tabId and can monitor
    // the real training tab. In that case the app does NOT open the tab here;
    // the Companion opens it via the handoff below. If the Companion fails to
    // launch (rare — the install gate guarantees it is installed), we fall back
    // to opening the tab ourselves (navigating the popup-safe placeholder when
    // present, else a best-effort window.open).
    const delegateToCompanion = !!options.delegateProviderTabToCompanion;
    const openProviderTabNow = () => {
      if (preOpenedWindow && !preOpenedWindow.closed) {
        try {
          preOpenedWindow.opener = null; // noopener-equivalent
        } catch {
          /* cross-origin / frozen handle — ignore */
        }
        preOpenedWindow.location.replace(options.targetUrl);
        console.log('[OpenTraining] launch: navigated pre-opened tab to provider');
      } else {
        const win = window.open(options.targetUrl, '_blank', 'noopener,noreferrer');
        if (!win) {
          // Popup blocked (common on Safari) → surface a direct-open fallback
          // (a plain anchor the user can click as a fresh gesture). Opening it
          // does NOT mark the training complete.
          useTrainingLaunchFallbackStore.getState().show(options.targetUrl, 'popup_blocked');
          console.log('[OpenTraining] launch: window.open blocked → showing direct-open fallback');
        } else {
          console.log('[OpenTraining] launch: opened provider tab (direct)');
        }
      }
    };
    if (!options.skipWindowOpen && !delegateToCompanion) {
      openProviderTabNow();
    }
    
    // STEP 2: Fire-and-forget session creation + event logging
    // Wrapped in async IIFE so all awaits happen AFTER window.open
    (async () => {
      let sessionId: string | null = null;

      // K108-Y RESUME: reuse the existing pending session — do NOT insert a new row
      // (no duplicate History). Fall through to mint (resume:true) + Companion
      // handoff below so resumed evidence appends to this same session.
      if (options.existingSessionId) {
        sessionId = options.existingSessionId;
        console.log('[OpenTraining] Resuming existing session:', sessionId);
      }

      try {
        // Core-100 R4 wave 2 CORE-O (Rel): one launch act = one history row. The launch key is generated once for
        // this act; a retried insert (transient failure / double-submit) reuses the row instead of adding another.
        const launched = options.existingSessionId
          ? null
          : await insertLaunchSessionOnce(supabase, {
            launch_key: newLaunchKey(),
            technician_profile_id: options.technicianProfileId,
            artifact_id: options.artifactId,
            surface: options.surface,
            source: options.source,
            expected_duration_minutes: options.expectedDurationMinutes,
            completion_status: 'pending',
            completion_confidence: 'unverified',
            // Batch 18-E4: promote ticket context out of metadata into a
            // real FK column; capture the fine-grained launch surface.
            ticket_id: options.ticketId ?? null,
            launch_source: options.launchSource ?? null,
            // Batch 18-E5: snapshot the exact URL window.open() received
            // so historical evidence survives later edits to the
            // artifact's access_url / purchase_url. Stored verbatim —
            // no normalization, no canonicalization.
            opened_url_snapshot: options.targetUrl ?? null,
            metadata: {
              artifact_type: options.artifactType,
              ...(options.intent ? { intent: options.intent } : {}),
              ...(options.jobCategory ? { job_category: options.jobCategory } : {}),
            },
          });

        if (launched?.ok) {
          sessionId = launched.sessionId;
          console.log('[OpenTraining] Session created:', sessionId);
          // Core-100 R3 CORE-O: the person's history reads this durable record.
          void queryClient.invalidateQueries({ queryKey: ['learning-history', options.technicianProfileId] });
        } else if (launched && launched.ok === false) {
          console.warn('[OpenTraining] Failed to create session:', launched.message);
          // Core-100 R4 CORE-O: the server refused a retired training (R2-37 guard). Say so plainly instead of
          // leaving the person in a provider tab that will never be recorded as their learning.
          if (launched.retired) {
            toast.error('This training has been retired', {
              description: 'It is no longer offered, so this visit is not recorded in your learning history.',
            });
            if (preOpenedWindow && !preOpenedWindow.closed) {
              try { preOpenedWindow.close(); } catch { /* ignore */ }
            }
            return;
          }
        }
      } catch (err) {
        console.warn('[OpenTraining] Exception creating session:', err);
      }
      
      // STEP 3: Log 'opened' with sessionId (fire-and-forget, window already open)
      logOpenedFireAndForget({
        technicianProfileId: options.technicianProfileId,
        artifactId: options.artifactId,
        source: options.source,
        surface: options.surface,
        sessionId: sessionId || undefined,
        ticketId: options.ticketId,
        jobIds: options.jobIds,
        jobCategory: options.jobCategory,
        metadata: {
          expected_duration_minutes: options.expectedDurationMinutes,
          artifact_type: options.artifactType,
        },
      });

      // STEP 4: Best-effort Training Companion token mint (Batch 18-F9B).
      // Mints a one-time plaintext token via mint-training-session-token
      // Edge Function so the future Training Companion extension
      // (18-F10) can authenticate against record-training-extension-
      // evidence (18-F8). Strict non-blocking semantics:
      //   - window.open already fired in step 1, so a failure here has
      //     zero user impact
      //   - Mint runs ONLY when a sessionId actually exists (skipped on
      //     INSERT failure)
      //   - Errors are caught, logged without the token/hash, and
      //     swallowed — never rethrown
      //   - The plaintext token is NEVER stored in localStorage /
      //     sessionStorage, never put in a URL, never console.log'd.
      //   - TCX-3 (K97): the token IS now handed to the Training Companion
      //     via the content-script bridge — `emitCompanionTokenHandoff`
      //     `window.postMessage`s it (strict same-origin targetOrigin) to the
      //     Companion content script, which forwards it to its own background
      //     (no extension id / no `externally_connectable` needed) and ACKs.
      //     The token is never logged here. The optional
      //     `onCompanionTokenMinted` callback still fires for caller-specific
      //     handling; with no Companion installed the postMessage is simply
      //     unhandled and the handoff resolves `{ connected: false }`.
      if (sessionId) {
        try {
          const tokenResult = await tokenMutation.mutateAsync({
            sessionId,
            resume: !!options.existingSessionId, // K108-Y: rotate the active token on resume
          });
          options.onCompanionTokenMinted?.(tokenResult);
          // TCX-3: emit to the Companion via the content-script bridge
          // (fire-and-forget; never blocks, never logs the token).
          void emitCompanionTokenHandoff({
            sessionId: tokenResult.sessionId,
            token: tokenResult.token,
            expiresAt: tokenResult.expiresAt,
            openedUrlSnapshot: options.targetUrl,
            // K108-P: ask the Companion to open+own the provider tab when we are
            // delegating; pass the approved-domain allowlist for validation.
            providerUrl: delegateToCompanion ? options.targetUrl : null,
            approvedDomains: options.approvedDomains ?? null,
            launchMode: delegateToCompanion ? 'companion_opens' : 'app_opens',
            expectedDurationSec:
              options.expectedDurationMinutes != null
                ? Math.floor(options.expectedDurationMinutes * 60)
                : null,
          }).then((handoff) => {
            console.log(
              '[OpenTraining] Companion token handoff:',
              handoff.connected ? 'acknowledged' : 'no companion (fallback)',
              delegateToCompanion ? `launched=${handoff.launched}` : '',
            );
            // K108-P delegated-launch fallback: if we delegated but the Companion
            // did not open the tab, open it ourselves (popup-safe placeholder if
            // present, else best-effort). If it DID launch and we had a
            // placeholder, close the now-redundant blank tab.
            if (delegateToCompanion) {
              if (!handoff.launched) {
                openProviderTabNow();
              } else if (preOpenedWindow && !preOpenedWindow.closed) {
                try { preOpenedWindow.close(); } catch { /* ignore */ }
              }
            }
          });
          // Log only sessionId — never the plaintext token or hash.
          console.log('[OpenTraining] Companion token minted for session:', sessionId);
        } catch (mintError) {
          const safeMessage =
            mintError instanceof Error ? mintError.message : 'unknown error';
          console.warn(
            '[OpenTraining] Companion token mint failed (non-blocking):',
            safeMessage,
          );
        }
      }

      // Invalidate session queries
      queryClient.invalidateQueries({
        queryKey: ['consumption-sessions', options.technicianProfileId],
      });
    })();
    }; // end runLaunch

    // The durable-consent-gated launch (unchanged contract).
    const gatedLaunch = () => {
      if (consentGate) {
        consentGate.gateMonitoredLaunch(runLaunch, {
          technicianProfileId: options.technicianProfileId,
          targetUrl: options.targetUrl,
        });
      } else {
        runLaunch();
      }
    };

    // K108-R Batch 1: show the per-launch disclosure FIRST (every launch). Its
    // "Agree & continue" click runs `gatedLaunch` synchronously — the cached
    // consent path then launches in-gesture (popup-safe), and the uncached path
    // defers to the consent modal (whose placeholder keeps Safari popup-safe).
    // "Cancel" does not launch. When the provider is unmounted (unit tests), the
    // launch proceeds directly.
    if (launchDisclosure) {
      launchDisclosure.requestLaunch(gatedLaunch);
    } else {
      gatedLaunch();
    }
  };

  return { openTraining };
}
